import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trackAssistantCommandRun } from '../models/assistant/analytics';
import useChatbotActionHandlers from './use-chatbot-action-handlers';
import { CommandResult } from '../models/chatbot/command-result';
import { Button } from '../models/assistant/interfaces/Button';

/**
 * Chatbot-specific command runner that doesn't maintain history.
 * Only keeps track of the last command result for display.
 */
const useChatbotCommandRunner = () => {
  const { handleAction } = useChatbotActionHandlers();
  const { t } = useTranslation();
  const [lastResult, setLastResult] = useState<CommandResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const runCommand = useCallback(
    (command: Button, analyticsData?: Record<string, unknown>) => {
      trackAssistantCommandRun(analyticsData);

      // Clear previous result and show loading
      setLastResult(null);
      setIsLoading(true);

      const actionResponse = handleAction(command.action);

      if (actionResponse) {
        // This is a request command that will return a response
        actionResponse
          .then((res) => res.data)
          .then((data) => {
            const item = data.data[0];
            if (item) {
              // Display only the result, not the command
              // Use metadata.button_text as the header if available
              setLastResult({
                headerText: data.metadata?.button_text,
                text: item.text,
                subject: item.subject,
              });
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('chatbot command runner error: ', err);

            // Show error message
            const message = err.response?.data?.errors?.[0]?.title;
            setLastResult({
              text: message || t('errors.unknown'),
              error: true,
            });
            setIsLoading(false);
          });
      } else {
        // Navigation command - no result to display
        setIsLoading(false);
      }
    },
    [handleAction, t]
  );

  return { runCommand, lastResult, isLoading };
};

export default useChatbotCommandRunner;
